import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Table, Button, Modal, Form, Input, message, Space, Select, DatePicker, Typography, Pagination, Dropdown } from 'antd';
import { HeartOutlined, HeartFilled, MoreOutlined } from '@ant-design/icons';
import { DebtTypeOptions, toSelectOptions, labelOfDebtType, IntentTypeOptions, labelOfDisposalMethod, formatTransferConditionText } from '../../constants/debtEnums';
import { useNavigate } from 'react-router-dom';
import { getSquareDebts, addFavorite as apiAddFavorite, removeFavorite as apiRemoveFavorite, submitIntent as apiSubmitIntent } from '../../services/debt';
import dayjs from 'dayjs';
import { formatAmountText } from '../../utils/numberFormat';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './BrowseDebtsPage.css';

/**
 * 债权广场-浏览
 * 职责：展示公开发布的债权列表，支持筛选、排序、收藏、提交投资/代理催收意向、查看详情。
 * 数据：从服务层拉取分页列表与详情；收藏/意向提交通过服务交互并更新本地状态。
 * 交互：表格分页排序、收藏切换、申请弹窗（表单校验与预填）、详情跳转。
 */
const ActionCell = ({ record, layoutKey, actionWidth, isMobile, onApply, onToggleFavorite }) => {
  const containerRef = useRef(null);

  const candidates = useMemo(() => ([
    { key: 'apply', label: '投资/代理' },
    { key: 'favorite', label: record?.favorited ? '取消收藏' : '收藏' },
  ]), [record?.favorited]);

  const [inlineKeys, setInlineKeys] = useState(() => candidates.map(a => a.key));

  useEffect(() => {
    setInlineKeys(candidates.map(a => a.key));
  }, [candidates, layoutKey]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const isOverflow = el.scrollWidth > el.clientWidth + 1;
    if (isOverflow && inlineKeys.length > 1) {
      const next = inlineKeys.slice(0, -1);
      if (next.join('|') !== inlineKeys.join('|')) {
        setInlineKeys(next);
      }
    }
  }, [candidates, inlineKeys]);

  const inlineActions = candidates.filter(a => inlineKeys.includes(a.key));
  const dropdownActions = candidates.filter(a => !inlineKeys.includes(a.key));

  const handleAction = async (key) => {
    if (key === 'apply') {
      onApply?.(record);
      return;
    }
    if (key === 'favorite') {
      await onToggleFavorite?.(record);
    }
  };

  return (
    <div
      ref={containerRef}
      className="c-actions-inline c-actions-cell"
      style={{ '--actions-cell-width': actionWidth }}
    >
      {inlineActions.map(action => {
        if (action.key === 'favorite') {
          return (
            <Button
              key={action.key}
              type="link"
              className="c-btn-link-compact"
              icon={record?.favorited ? <HeartFilled /> : <HeartOutlined />}
              onClick={() => handleAction(action.key)}
              aria-label={record?.favorited ? '已收藏' : '收藏'}
            >
              {isMobile ? '' : action.label}
            </Button>
          );
        }
        return (
          <Button
            key={action.key}
            type="link"
            className="c-btn-link-compact"
            onClick={() => handleAction(action.key)}
          >
            {action.label}
          </Button>
        );
      })}

      {dropdownActions.length > 0 && (
        <Dropdown
          trigger={['click']}
          menu={{
            items: dropdownActions.map(a => ({ key: a.key, label: a.label })),
            onClick: async ({ key }) => {
              await handleAction(key);
            },
          }}
        >
          <Button type="link" className="c-btn-link-compact c-actions-more" icon={<MoreOutlined />} aria-label="更多操作" />
        </Dropdown>
      )}
    </div>
  );
};

const BrowseDebtsPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 });
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyDebt, setApplyDebt] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [sorter, setSorter] = useState({ field: 'publish_time', order: 'desc' });
  const { user } = useSelector(state => state.auth || {});

  

  // 拉取列表：携带筛选与排序参数，更新分页与数据集
  const loadList = async (page = 1, pageSize = PAGINATION_DEFAULT_PAGE_SIZE, sort = sorter, knownTotal = pagination.total) => {
    try {
      setLoading(true);
      const effectivePageSize = normalizePageSize(pageSize);
      const values = searchForm.getFieldsValue();
      const params = {
        page,
        size: effectivePageSize,
        keyword: values.keyword || undefined,
        debt_type: values.debt_type || undefined,
        start_date: values.date_range && values.date_range[0] ? values.date_range[0].format('YYYY-MM-DD') : undefined,
        end_date: values.date_range && values.date_range[1] ? values.date_range[1].format('YYYY-MM-DD') : undefined,
        sort_field: sort.field,
        sort_order: sort.order,
        need_total: page === 1,
      };
      const { data } = await getSquareDebts(params);
      const list = Array.isArray(data?.records) ? data.records : [];
      const totalFromApi = data?.total;
      const total = Number(totalFromApi ?? knownTotal ?? 0);
      setRecords(list);
      setPagination(prev => ({ ...prev, current: page, pageSize: effectivePageSize, total }));
    } catch (e) {
      message.error('加载债权广场失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 查询与重置：重置到第一页，恢复默认排序并重新加载
  const handleSearch = () => { setPagination(prev => ({ ...prev, current: 1 })); loadList(1, pagination.pageSize, sorter); };
  const handleReset = () => { searchForm.resetFields(); setSorter({ field: 'publish_time', order: 'desc' }); loadList(1, pagination.pageSize, { field: 'publish_time', order: 'desc' }); };

  // 收藏切换：调用服务端接口并本地翻转 `favorited` 状态
  const toggleFavorite = async (record) => {
    try {
      if (record.favorited) {
        await apiRemoveFavorite(record.id);
        message.success('已取消收藏');
      } else {
        await apiAddFavorite(record.id);
        message.success('已加入收藏');
      }
      setRecords(prev => prev.map(r => r.id === record.id ? { ...r, favorited: !r.favorited } : r));
    } catch (e) {
      message.error('操作失败');
    }
  };

  // 打开申请弹窗：预填联系人信息（若用户已登录）
  const openApply = (record) => {
    setApplyDebt(record);
    setApplyOpen(true);
    form.resetFields();
    form.setFieldsValue({
      name: (user && (user.real_name || user.realName || user.username)) || '',
      phone: (user && (user.phone || user.contactPhone)) || '',
    });
  };

  // 提交申请：校验后提交联系方式与意向类型
  const submitApply = async () => {
    const values = await form.validateFields();
    const payload = {
      intent_type: values.intent_type,
      name: values.name,
      contact_phone: values.phone,
      message: values.remark,
    };
    try {
      await apiSubmitIntent(applyDebt.id, payload);
      setApplyOpen(false);
      message.success('申请已提交');
    } catch (e) {
      message.error(e.message || '提交失败');
    }
  };

  // 查看详情：跳转到统一的债权详情页
  const openDetail = (record) => {
    const id = record?.id;
    if (!id) return;
    navigate(`/debt-management/detail/${id}`);
  };

  const actionColumnWidth = isMobile ? 140 : 220;

  const columns = [
    {
      title: '债权名称',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (v, record) => (
        <Typography.Link className="u-ellipsis-inline font-medium" onClick={() => openDetail(record)}>
          {v || '-'}
        </Typography.Link>
      )
    },
    { title: '债权类型', dataIndex: 'debt_type', key: 'debt_type', width: 140, render: (v) => labelOfDebtType(v) },
    { title: '合计金额', dataIndex: 'total_amount', key: 'total_amount', width: 120, sorter: true, align: 'right', className: 'u-tabular-nums', render: (v) => formatAmountText(v) },
    { title: '债权发生日', dataIndex: 'debt_occurrence_date', key: 'debt_occurrence_date', width: 140, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: '处置方式', dataIndex: 'disposal_method', key: 'disposal_method', width: 140, render: (v) => labelOfDisposalMethod(v) },
    { title: '转让条件', key: 'transfer_condition', width: 220, ellipsis: true, render: (_, record) => formatTransferConditionText(record, { formatAmount: formatAmountText }) },
    { title: '发布时间', dataIndex: 'publish_time', key: 'publish_time', width: 160, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    { title: '操作', key: 'action', fixed: 'right', width: actionColumnWidth, render: (_, record) => (
      <ActionCell
        record={record}
        layoutKey={isMobile ? 'mobile' : 'desktop'}
        actionWidth={isMobile ? '140px' : '220px'}
        isMobile={isMobile}
        onApply={openApply}
        onToggleFavorite={toggleFavorite}
      />
    ) },
  ];

  // 表格交互：同步分页与排序并重新请求数据
  const handleTableChange = (_pag, _filters, sorterArg) => {
    const nextOrder = sorterArg?.order ? (sorterArg.order === 'ascend' ? 'asc' : 'desc') : sorter.order;
    const nextField = sorterArg?.field || sorter.field;
    const nextSorter = { field: nextField, order: nextOrder };
    setSorter(nextSorter);
    loadList(pagination.current, pagination.pageSize, nextSorter, pagination.total);
  };

  const handlePaginationChange = (page, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const shouldResetToFirstPage = nextPageSize !== pagination.pageSize;
    const nextCurrent = shouldResetToFirstPage ? 1 : page;
    setPagination(prev => ({ ...prev, current: nextCurrent, pageSize: nextPageSize }));
    loadList(nextCurrent, nextPageSize, sorter, pagination.total);
  };

  return (
    <div className="page-container p-debt-marketplace-browse">
      <div className="page-content page-content-wide space-y-md">
        <div className="page-title">债权广场</div>

        <Card className="card-compact">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={searchForm} onFinish={handleSearch}>
            <Form.Item name="keyword" label="关键词">
              <Input
                placeholder="债权名称/编号"
                allowClear
                style={{ width: isMobile ? '100%' : 200 }}
              />
            </Form.Item>
            <Form.Item name="debt_type" label="类型">
              <Select
                allowClear
                options={toSelectOptions(DebtTypeOptions)}
                style={{ width: isMobile ? '100%' : 180 }}
              />
            </Form.Item>
            <Form.Item name="date_range" label="发生日">
              <DatePicker.RangePicker
                allowEmpty={[true, true]}
                style={{ width: isMobile ? '100%' : undefined }}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">查询</Button>
                <Button onClick={handleReset}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card className="card-compact">
          <Table
            rowKey="id"
            loading={loading}
            dataSource={records}
            columns={columns}
            size={isMobile ? 'small' : 'middle'}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
          />
          <div className="c-pagination-bar">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              simple={isMobile}
              showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              onChange={handlePaginationChange}
            />
          </div>
        </Card>

        <Modal open={applyOpen} title="提交联系方式" onOk={submitApply} onCancel={() => setApplyOpen(false)} okText="提交" cancelText="取消">
          <Form form={form} layout="vertical">
            <Form.Item name="intent_type" label="意向类型" rules={[{ required: true, message: '请选择意向类型' }]}>
              <Select options={IntentTypeOptions} />
            </Form.Item>
            <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
              <Input placeholder="请输入姓名" />
            </Form.Item>
            <Form.Item name="phone" label="联系电话" rules={[{ required: true, message: '请输入联系电话' }]}>
              <Input placeholder="请输入联系电话" />
            </Form.Item>
            <Form.Item name="remark" label="备注说明">
              <Input.TextArea rows={3} placeholder="可填写投资/催收意向说明" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default BrowseDebtsPage;
